import CloseIcon from "@mui/icons-material/Close";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import { paymentManagementStatusOptions } from "Common/Components/StatusDropdown/statusOptions";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportServicePaymentManagementCSV,
  fetchServicePaymentManagementByType,
} from "slices/paymentmanagement/thunk";
// import FollowupModel from "pages/PaymentManagement/FollowupModel";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import FollowupModel from "components/FollowUpModal/FollowUpModel";
import FollowUpPopUp from "components/FollowUpModal/FollowUpPopUp";
import {
  SALES_ORDER_CATEGORY,
  SALES_ORDER_TYPE,
} from "Constants/StatusConstant";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import usePermission from "hooks/usePermission";
import DOMPurify from "dompurify";
import Chip from "components/Chips/Chip";
type RetentionPaymentProps = {
  activeTab: string;
};

const RetentionPayment: React.FC<RetentionPaymentProps> = ({ activeTab }) => {
  document.title = "Payment Management";
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.payment_management?.app_name,
    app_modules?.payment_management?.modules?.service
  );

  const tableState = usePreservedTableState({
    key: TableKeys.PAYMENT_MANAGEMENT.SERVICE.RETENTION,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectPaymentManagementData = createSelector(
    (state: any) => state.PaymentManagement,
    (paymentmanagement) => ({
      rowCount: paymentmanagement?.rowCount || 0,
    })
  );

  const { rowCount } = useSelector(selectPaymentManagementData);

  const handleFetchPaymentManagement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sort = sorting[0]?.id;
      const order = sorting[0]?.desc ? "desc" : "asc";

      const response = await dispatch(
        fetchServicePaymentManagementByType({
          activeTab: activeTab || "Retention Payment",
          page: pageIndex + 1,
          pageSize,
          sort,
          search,
          order,
          filters: filterValue,
        })
      );
      setData(response.payload.data);
    } catch (error: any) {
      console.error("Error fetching payment management data:", error);
      setError(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [dispatch, activeTab, pageIndex, pageSize, sorting, filterValue, search]);

  useEffect(() => {
    handleFetchPaymentManagement();
  }, [handleFetchPaymentManagement]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const handleStatusChange = async (
    salesOrderId: string,
    newStatus: string
  ) => {
    // await dispatch(
    // fetchSalesOrderStatusUpdate({ id: salesOrderId, status: newStatus })
    // ); have to add thunk to change status update for payment
    console.log("salesOrderId", salesOrderId);
    console.log("newStatus", newStatus);
  };

  const columns = useMemo(() => {
    return [
      // {
      //   header: "Sr.No",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => row.original.sr_no,
      // },
      {
        header: "SRV No.",
        accessorKey: "sales_order_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl:
          API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN("sales_order_number"),
        dropdownMap: (data: any[]) =>
          data.map((item) => ({ label: item.label, value: item.value })),
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => row.original.sales_order_number ?? "-",
      },
      {
        header: "Service Number",
        accessorKey: "service_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterFetchUrl:
          API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
            "service_number",
            "retention"
          ),
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => row.original.service_number ?? "-",
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterFetchUrl:
          API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
            "customer_name",
            "retention"
          ),
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => row.original.customer_name ?? "-",
      },
      // {
      //   header: "Total Amount",
      //   accessorKey: "total_amount",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }: { row: any }) =>
      //     row.original.total_amount != null
      //       ? `₹${row.original.total_amount?.toLocaleString()}`
      //       : "-",
      // },
      {
        header: "Retention Amount",
        accessorKey: "retention_amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(row.original.retention_amount)}`;
        },
      },
      // {
      //   header: "Validity",
      //   accessorKey: "validity",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "date" as const,
      //   cell: ({ row }: { row: any }) => (
      //     <FormattedDate date={row.original.validity} />
      //   ),
      // },
      {
        header: "Received Amount",
        accessorKey: "received_amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(row.original.received_amount)}`;
        },
      },
      {
        header: "Pending Amount",
        accessorKey: "pending_amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(row.original.pending_amount)}`;
        },
      },
      {
        header: "Status",
        accessorKey: "retention_payment_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN("retention_payment_status"),
        cell: ({ row }: { row: any }) => {
          const statusValue = row.original.retention_payment_status;
          return <StatusChip status={statusValue || "Unpaid"} />;
        },
      },
      {
        header: "Last Follow Up Date",
        accessorKey: "follow_up_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl:
          API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
            "follow_up_date",
            "retention"
          ),
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.follow_up_date} />
        ),
      },
      {
        header: "Follow Up Remark",
        accessorKey: "follow_up_remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const rawHtml = row.original?.follow_up_remark || "-";

          if (!rawHtml) return <span>-</span>;

          const safeHtml = DOMPurify.sanitize(rawHtml);

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <div
                  style={{
                    maxWidth: "450px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                    p: 1.5,
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "200px",
                  maxWidth: "400px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  lineHeight: "1.5",
                  maxHeight: "6em", // 4 lines * 1.5 line-height
                  cursor: "pointer",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </Tooltip>
          );
        },
      },
      {
        header: "Next Follow Up Date",
        accessorKey: "next_follow_up_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl:
          API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
            "next_follow_up_date",
            "retention"
          ),
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.next_follow_up_date} />
        ),
      },
      // {
      //   header: "Assigned To",
      //   accessorKey: "assigned_members",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => {
      //     const members = row.original?.assigned_members || [];

      //     if (!members.length) return "-";

      //     const names = members.map(
      //       (m: any) =>
      //         `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
      //     );

      //     const visible = names.slice(0, 4);
      //     const remaining = names.length - visible.length;

      //     return (
      //       <Tooltip
      //         arrow
      //         placement="top"
      //         title={names.join("\n")}
      //         componentsProps={{
      //           tooltip: {
      //             sx: {
      //               whiteSpace: "pre-line",
      //               maxWidth: 250,
      //               fontSize: "13px",
      //             },
      //           },
      //         }}
      //       >
      //         <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
      //           {visible.map((name: string, index: number) => (
      //             <div key={index}>{name}</div>
      //           ))}

      //           {remaining > 0 && (
      //             <div style={{ fontSize: "12px", color: "#6c757d" }}>
      //               +{remaining} more
      //             </div>
      //           )}
      //         </div>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems = [
            {
              icon: <PaymentsIcon fontSize="small" color="primary" />,
              label: "Record Payment",
              permission: canUpdate,
              onClick: (item: any) => {
                navigate(`/payment-management/service/retention/record-payment/${data.sales_order_service_id}`);
              },
              color: "primary",
            },
            {
              icon: <PersonAddIcon fontSize="small" color="success" />,
              label: "Follow-Up",
              permission: canUpdate,
              onClick: (item: any) => {
                setSelectedFollowUpItem(data);
                setShowFollowUpModal(true);
              },
              color: "primary",
            },
          ];
          return <DynamicMenuItem row={data} menuItems={menuItems} />;
        },
      },
    ].filter((column) => column.header !== "Action" || canUpdate);
  }, [navigate, canUpdate]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            <ExportButton
              onExport={async () => {
                return await exportServicePaymentManagementCSV({
                  activeTab: activeTab || "Retention Payment",
                  search,
                  filters: filterValue,
                });
              }}
            />
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <LoaderErrorWrapper loading={loading} error={error}>
          <MasterTableContainer
            columns={columns}
            data={data || []}
            totalRecords={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        </LoaderErrorWrapper>
      </div>

      <Dialog
        open={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: "95vw",
            maxWidth: "95vw",
            height: "100vh",
            borderRadius: "12px",
          },
        }}
        scroll="paper"
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            fontWeight: "bold",
            fontSize: "0.95rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          PBG Follow-up and Activities
          <IconButton
            aria-label="close"
            onClick={() => setShowFollowUpModal(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
              fontSize: "0.95rem",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
          {selectedFollowUpItem && (
            <FollowUpPopUp
              open={showFollowUpModal}
              onClose={() => {
                setShowFollowUpModal(false);
                handleFetchPaymentManagement();
              }}
              title="Follow-up and Activities"
            >
              {selectedFollowUpItem && (
                <FollowupModel
                  data={{
                    ...selectedFollowUpItem,
                    sales_order_service_id: selectedFollowUpItem.sales_order_service_id || selectedFollowUpItem.id,
                  }}
                  activeType={SALES_ORDER_TYPE?.SERVICE}
                  category={SALES_ORDER_CATEGORY?.RETENTION}
                  onClose={() => {
                    setShowFollowUpModal(false)
                    handleFetchPaymentManagement()
                  }}
                  showAssignedMembers={false}
                />
              )}
            </FollowUpPopUp>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RetentionPayment;
